/** THE CHAT'S MODEL SWITCH SHOWS THE MODEL THAT RUNS (Tom, 2026-10-14: "it
 *  currently says Argon but it's 3.8"; "No 'Automatic'"; "if it flips to
 *  flash - it should change the user option and the user should need to
 *  manually switch back").
 *
 *  Two settings: the primary (a new chat's) or the backup. A choice is saved
 *  on the task when it is made (`model_preference`), never on every send. A
 *  fallback within a turn changes the chat's choice to the backup on the
 *  server; the switch follows the task's own `model_preference`, so it then
 *  shows the backup until the user switches back.
 *
 *  What it shows:
 *  - during a turn, the task's own `model_name` / `model_reason`, which the
 *    server writes as the turn resolves its model (and on a fallback within
 *    the turn) — they arrive live with the task;
 *  - between turns, GET /tasks/{id}/model-status: the model the NEXT turn
 *    runs on and why. Read again when a turn ends, when the window regains
 *    focus, when the switch is opened and after a choice is saved.
 *  A line under the switch says why when a fallback switched it, and which
 *  model this turn still runs on when a choice made mid-turn applies from
 *  the next one. */

import { useCallback, useEffect, useRef, useState } from 'react';

export type ModelPreference = 'primary' | 'backup';
export type ModelReason = 'chosen' | 'default' | 'fallback' | 'switched';

export type ModelOption = { preference: ModelPreference; label: string; modelName: string };

/** The model in use and why (`stalledAt`: epoch seconds). */
export type ModelInUse = {
  model: string;
  reason: ModelReason | null;
  stalled: string | null;
  stalledAt: number | null;
};

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const text = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const REASONS: ModelReason[] = ['chosen', 'default', 'fallback', 'switched'];
const reasonOf = (v: unknown): ModelReason | null => (REASONS.includes(v as ModelReason) ? (v as ModelReason) : null);

/** GET /tasks/{id}/model-status's answer → ModelInUse (null when it names no model). */
export function fromStatus(body: any): ModelInUse | null {
  const model = text(body?.model);
  if (!model) return null;
  return { model, reason: reasonOf(body.reason), stalled: text(body.stalled), stalledAt: num(body.stalled_at) };
}

/** The task's own fields (what its current turn runs on) → ModelInUse. */
export function fromTask(task: any): ModelInUse | null {
  const model = text(task?.model_name);
  if (!model) return null;
  return { model, reason: reasonOf(task.model_reason), stalled: text(task.model_stalled), stalledAt: num(task.model_stalled_at) };
}

/** During a turn the task's fields; between turns the next turn's status
 *  (the task's fields until it has answered). */
export function modelInUse(task: any, nextTurn: ModelInUse | null, turnRunning: boolean): ModelInUse | null {
  const running = fromTask(task);
  return turnRunning ? running ?? nextTurn : nextTurn ?? running;
}

/** The switch's setting: the task's choice; no choice yet is the primary. */
export const switchValueOf = (pref: unknown): ModelPreference => (pref === 'backup' ? 'backup' : 'primary');

const hhmm = (s: number) => new Date(s * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** The line under the switch, or null when the model in use needs no word. */
export function modelNotice(inUse: ModelInUse | null, options: ModelOption[], value: ModelPreference): string | null {
  if (!inUse) return null;
  const primary = options.find((o) => o.preference === 'primary')?.modelName;
  if (inUse.reason === 'fallback' || inUse.reason === 'switched') {
    const stalled = inUse.stalled ?? primary ?? 'the main model';
    const at = inUse.stalledAt ? ` at ${hhmm(inUse.stalledAt)}` : '';
    return `Switched to ${inUse.model}: ${stalled} timed out${at}. Switch back to try it again.`;
  }
  // a choice made during a turn applies from the next one: say what runs now
  const chosen = options.find((o) => o.preference === value)?.modelName;
  if (chosen && inUse.model !== chosen) return `This turn runs on ${inUse.model}; the next one on ${chosen}.`;
  return null;
}

const API = () => process.env.NEXT_PUBLIC_SOURCEPILOT_URL;

/** The switch's state for one chat. `save` writes a choice on the task. */
export function useModelSwitch(task: any, taskId: string | null | undefined, turnRunning: boolean,
  save: (pref: ModelPreference) => Promise<void>) {
  const [options, setOptions] = useState<ModelOption[]>([]);
  // a choice made here shows at once and until the task's own choice next
  // changes (the saved one arriving, or a fallback changing it on the server)
  const [chosen, setChosen] = useState<ModelPreference | null>(null);
  const taskPref = task?.model_preference;
  useEffect(() => { setChosen(null); }, [taskPref]);
  const [nextTurn, setNextTurn] = useState<ModelInUse | null>(null);
  const [said, setSaid] = useState<string | null>(null);

  // the model pair (global for now — see GET /tasks/model-options); no
  // switch when the fetch fails or no distinct backup is configured
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API()}/tasks/model-options`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const opts: ModelOption[] = [];
        if (data.primary) opts.push({ preference: 'primary', label: data.primary.model_name, modelName: data.primary.model_name });
        if (data.backup && data.backup.model_name !== data.primary?.model_name) {
          opts.push({ preference: 'backup', label: data.backup.model_name, modelName: data.backup.model_name });
        }
        setOptions(opts);
      } catch {
        /* leave the switch hidden on error */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const latest = useRef(0);
  const refresh = useCallback(async () => {
    if (!taskId) return;
    const ask = ++latest.current;
    try {
      const res = await fetch(`${API()}/tasks/${encodeURIComponent(taskId)}/model-status`, { credentials: 'include' });
      if (!res.ok) return;
      const status = fromStatus(await res.json());
      if (ask === latest.current) setNextTurn(status);
    } catch {
      /* the task's own fields stand */
    }
  }, [taskId]);

  // what a failed save said lasts until the next turn starts
  useEffect(() => { if (turnRunning) setSaid(null); }, [turnRunning]);
  // read on open, and again when a turn ends
  useEffect(() => { if (!turnRunning) void refresh(); }, [turnRunning, refresh]);
  // a change made elsewhere: caught up on focus
  useEffect(() => {
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const value: ModelPreference = chosen ?? switchValueOf(taskPref);
  const inUse = modelInUse(task, nextTurn, turnRunning);

  const choose = useCallback(async (next: ModelPreference) => {
    setChosen(next);
    setSaid(null);
    try {
      await save(next);
    } catch {
      setChosen(null);
      setSaid('The model choice was not saved. Try again.');
    }
    await refresh();
  }, [save, refresh]);

  return { options, value, inUse, notice: said ?? modelNotice(inUse, options, value), choose, onOpen: refresh };
}
